import Link from 'next/link';
import { useRouter } from 'next/router';
import { forwardRef, type HTMLAttributes, useContext } from 'react';

import { useMaskLink } from '@tmh-website/services/link.service';
import { determineLinkState } from '@tmh-website/services/navigation-state.service';
import { PageContext } from '@tmh-website/shared';
import { LinkField, NavigationHighlightLinkGroup, Page } from '@tmh-website/shared/cms';
import type { LocalePrefix } from '@tmh-website/shared/const';
import { convertLinkFieldAttributesArrayToObject } from '@tmh-website/shared/helpers';

import * as Styled from './NavigationLink.styles';

interface NavigationLinkButtonProps extends HTMLAttributes<HTMLAnchorElement> {
  href?: string;
  label: string;
  disabled: boolean;
  maskLink: boolean;
  isActive: boolean;
}

const NavigationLinkButton = forwardRef<HTMLAnchorElement, NavigationLinkButtonProps>(
  ({ href, maskLink, label, disabled, isActive = false, ...props }, ref) => {
    const { renderedHref, onMouseEnter, onMouseLeave } = useMaskLink(maskLink, href);
    return (
      <Styled.ClickArea
        {...props}
        href={renderedHref}
        as={renderedHref ? 'a' : 'button'}
        onMouseEnter={e => {
          if (onMouseEnter) onMouseEnter();
          if (props.onMouseEnter) props.onMouseEnter(e);
        }}
        onMouseLeave={e => {
          if (onMouseLeave) onMouseLeave();
          if (props.onMouseLeave) props.onMouseLeave(e);
        }}
        style={{ display: disabled ? 'none' : undefined }}
        ref={ref}>
        <Styled.NavLink $isActive={isActive} aria-disabled={disabled ? 'true' : undefined}>
          {label}
        </Styled.NavLink>
      </Styled.ClickArea>
    );
  }
);

NavigationLinkButton.displayName = 'NavigationLinkButton';

interface NavigationLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  link: LinkField;
  disabled?: boolean;
  /**
   * Mask the link using base64 encoding. This is used for SEO optimization
   */
  maskLink?: boolean;
  /**
   * This only exists to fix a type error in Navigation.tsx - do not remove!
   */
  locale?: LocalePrefix;
  visibleHighlightLinkGroups: NavigationHighlightLinkGroup[];
}

const NavigationLink = ({
  link,
  disabled = false,
  maskLink = false,
  visibleHighlightLinkGroups,
  ...props
}: NavigationLinkProps) => {
  const page = useContext(PageContext) as Page;
  const { locale } = useRouter();
  const href = link.value;

  const state = determineLinkState(page.path, link.value, visibleHighlightLinkGroups, locale, 'navigation-link');

  const { locale: filteredLocale, ...restProps } = props;

  const additionalAttributes = convertLinkFieldAttributesArrayToObject(link.attributes);

  if (href) {
    return (
      <Link href={href} locale={locale} passHref legacyBehavior>
        <NavigationLinkButton
          label={link.label}
          disabled={disabled}
          isActive={state}
          maskLink={maskLink}
          {...restProps}
          {...additionalAttributes}
        />
      </Link>
    );
  } else return null;
};

NavigationLink.displayName = 'NavigationLink';

export default NavigationLink;
